import * as Headless from '@headlessui/react';
import { EllipsisVertical } from 'lucide-react';
import cn from 'classnames';
import { GridAction } from './grid-action-controller';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';

type RowActionsCellProps<T> = {
  row: T;
  actions: GridAction<T>[];
  onExecute: (action: GridAction<T>, row: T) => void;
};

export function RowActionsCell<T>({
  row,
  actions,
  onExecute
}: RowActionsCellProps<T>) {
  const { getColour } = useThemeContext();
  const visible = actions.filter(a => (a.isVisible ? a.isVisible(row) : true));
  if (!visible.length) return null;

  const stickyActions = visible.filter(a => a.sticky);
  const menuActions = visible.filter(a => !a.sticky);

  const useMenuOnly = menuActions.length > 2;

  const renderStickyButtons = () =>
    stickyActions.map(a => {
      const disabled = a.isDisabled?.(row);
      const active = a.isActive?.(row) ?? false;
      const title =
        typeof disabled === 'string' ? disabled : (a.label ?? 'Action');
      return (
        <button
          key={a.id}
          type="button"
          className={cn(
            'p-1 rounded-2xl transition-opacity duration-150',
            active
              ? 'opacity-100'
              : 'opacity-0 group-hover:opacity-50 hover:!opacity-100',
            active
              ? 'text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20'
              : 'text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800',
            disabled && 'cursor-not-allowed'
          )}
          title={title}
          disabled={!!disabled}
          onClick={e => {
            e.stopPropagation();
            onExecute(a, row);
          }}
        >
          {a.icon ?? <span className="text-xs">{a.label}</span>}
        </button>
      );
    });

  if (!useMenuOnly && menuActions.length > 0) {
    return (
      <div className="flex items-center justify-end gap-1">
        {menuActions.map(a => {
          const disabled = a.isDisabled?.(row);
          const title =
            typeof disabled === 'string' ? disabled : (a.label ?? 'Action');
          return (
            <button
              key={a.id}
              type="button"
              className={cn(
                'p-1.5 rounded-2xl transition',
                a.intent === 'danger'
                  ? 'text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20'
                  : 'text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20',
                disabled && 'opacity-50 cursor-not-allowed'
              )}
              title={title}
              disabled={!!disabled}
              onClick={e => {
                e.stopPropagation();
                onExecute(a, row);
              }}
            >
              {a.icon ?? <span className="text-xs">{a.label}</span>}
            </button>
          );
        })}
        {renderStickyButtons()}
      </div>
    );
  }

  // > 2 menu actions -> use dropdown; sticky actions render inline before ⋮
  return (
    <div className="flex items-center justify-center gap-0.5">
      {renderStickyButtons()}

      <Headless.Menu as="div" className="relative">
        <Headless.Menu.Button
          className="p-1 rounded-2xl hover:bg-zinc-100 dark:hover:bg-zinc-800"
          onClick={e => e.stopPropagation()}
          aria-label="Row actions"
        >
          <EllipsisVertical className="h-5 w-5 text-zinc-600 dark:text-zinc-400" />
        </Headless.Menu.Button>

        <Headless.Portal>
          <Headless.Menu.Items
            anchor="bottom end"
            className="
              z-[1000]
              border
              rounded-2xl shadow-lg
              w-auto min-w-48 max-w-xs py-1
              focus:outline-none
              [--anchor-gap:0.25rem] [--anchor-padding:0.5rem]
            "
            style={{
              backgroundColor: getColour('thirdLayer'),
              borderColor: getColour('border'),
              color: getColour('normalText')
            }}
          >
            {menuActions.map(a => {
              const disabledReason = a.isDisabled?.(row);
              const title =
                typeof disabledReason === 'string'
                  ? disabledReason
                  : (a.label ?? 'Action');
              return (
                <Headless.Menu.Item key={a.id} disabled={!!disabledReason}>
                  {({ active, disabled }) => (
                    <button
                      className={cn(
                        'w-full text-left px-3 py-2 text-sm flex items-center gap-2 whitespace-nowrap',
                        active && 'bg-zinc-100 dark:bg-zinc-800',
                        disabled && 'opacity-50 cursor-not-allowed',
                        a.intent === 'danger' && 'text-red-600'
                      )}
                      style={
                        a.intent !== 'danger'
                          ? { color: getColour('normalText') }
                          : undefined
                      }
                      title={title}
                      onClick={e => {
                        e.stopPropagation();
                        if (!disabled) onExecute(a, row);
                      }}
                    >
                      {a.icon && <span className="shrink-0">{a.icon}</span>}
                      <span className="flex min-w-0 flex-col items-start">
                        <span>{a.label}</span>
                        {typeof disabledReason === 'string' && (
                          <span className="max-w-60 whitespace-normal text-xs font-normal leading-4">
                            {disabledReason}
                          </span>
                        )}
                      </span>
                    </button>
                  )}
                </Headless.Menu.Item>
              );
            })}
          </Headless.Menu.Items>
        </Headless.Portal>
      </Headless.Menu>
    </div>
  );
}
